import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { HumanResourcesItem } from '@/app/models/detailsRH';
import { useUpdateHumanResourcesMutation } from '@/app/services/detailsHRApi';
import { useSearchSchoolLavelQuery } from '@/app/services/financialClassificationApi';
import formatterMonetaryValue, { cpfOrCpnjFromBack } from '@/app/utils/numberUtils';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import TimeInput from '@/components/common/TimeImput';
import useFormattedCpfAndCpnj from '@/hooks/useFormattedCpfAndCpnj';
import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import FormFieldSelect from '../baseForms/FormFieldSelect';

interface NewEditHumanResourcesProps {
  selectItem: HumanResourcesItem | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditHumanResourcesDetails(props: NewEditHumanResourcesProps) {
  const theme = useTheme();
  const { t } = useTranslation(['humanResource', 'common']);

  const { selectItem, setDrawerOpen } = props;

  const { value: cpf } = useFormattedCpfAndCpnj(selectItem?.cpf);

  const [graduationCourse, setGraduationCourse] = useState<string>(
    selectItem?.graduationCourseCode ? selectItem?.graduationCourseCode : '',
  );

  const [graduationLevel, setGraduationLevel] = useState<string>(
    selectItem?.graduationLevel ? selectItem?.graduationLevel?.id.toString() : '',
  );

  const { data: schoolLevel } = useSearchSchoolLavelQuery();

  const [activityDescription, setActivityDescription] = useState<string>(
    selectItem?.descriptionActivities ? selectItem?.descriptionActivities : '',
  );

  const [workedHours, setWorkedHours] = useState<number>(
    selectItem?.monthlyJourney ? selectItem.monthlyJourney : DEFAULTVALUE,
  );
  const [extraHours, setExtraHours] = useState<string>(selectItem?.extraHours ? selectItem.extraHours : '0:0');

  const [update] = useUpdateHumanResourcesMutation();

  const handleSubmit = async () => {
    if (!selectItem) return;
    setDrawerOpen(false);
    await toast.promise(
      update({
        id: selectItem?.id as number,
        cpf: cpfOrCpnjFromBack(cpf),
        graduationCourseCode: graduationCourse,
        graduationLevel: {
          id: Number(graduationLevel),
        },
        extraHours,
        monthlyJourney: workedHours,
        descriptionActivities: activityDescription,
        personBadge: selectItem?.badge,
        personName: selectItem?.name,
        position: selectItem?.positionCode,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('hr.updated.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('common:edit.detail')} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('common:collaborator')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={selectItem?.name}
                  disabled
                />
              </Stack>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('cpf')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={cpf}
                  disabled
                />
              </Stack>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('common:position')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={selectItem?.positionCode}
                  disabled
                />
              </Stack>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('graduation.level')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={selectItem?.contractGraduationLevel ? selectItem?.contractGraduationLevel.toUpperCase() : ''}
                  disabled
                />
              </Stack>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />
        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
              {t('salary')}
            </Typography>
            <Typography variant="body1" sx={{ width: theme.spacing(12), mb: 0 }}>
              {formatterMonetaryValue(selectItem?.salary || 0)}
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
              {t('awards')}
            </Typography>
            <Typography variant="body1" sx={{ width: theme.spacing(12), mb: 0 }}>
              {formatterMonetaryValue(selectItem?.awards || 0)}
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
              {t('benefits')}
            </Typography>
            <Typography variant="body1" sx={{ width: theme.spacing(12), mb: 0 }}>
              {formatterMonetaryValue(selectItem?.benefits || 0)}
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
              {t('charges')}
            </Typography>
            <Typography variant="body1" sx={{ width: theme.spacing(12), mb: 0 }}>
              {formatterMonetaryValue(selectItem?.charges || 0)}
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
              {t('total')}
            </Typography>
            <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
              {formatterMonetaryValue(
                (
                  (selectItem?.benefits || 0) +
                  (selectItem?.charges || 0) +
                  (selectItem?.awards || 0) +
                  (selectItem?.salary || 0)
                ).toFixed(2),
              )}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />
        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Typography sx={{ width: theme.spacing(12), mb: 0 }} variant="fieldHeader">
              {t('graduation.course')}
            </Typography>
            <TextField
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              size="small"
              value={graduationCourse}
              onChange={(e) => setGraduationCourse(e.target.value)}
            />
          </Stack>
        </Box>

        <FormFieldSelect
          label={t('graduation.level')}
          value={graduationLevel}
          onChange={setGraduationLevel}
          options={schoolLevel ?? []}
          placeholder={t('select.school.level') as string}
        />

        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Typography sx={{ width: theme.spacing(12), mb: 0 }} variant="fieldHeader">
              {t('worked.hours')}
            </Typography>
            <TextField
              type="number"
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              size="small"
              value={workedHours}
              onChange={(e) => setWorkedHours(parseFloat(e.target.value))}
            />
          </Stack>
        </Box>

        <TimeInput value={extraHours} onChange={setExtraHours} label={t('extra.hours') as string} />

        <Box sx={{ mb: 2, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}
          >
            <Typography sx={{ width: theme.spacing(12), mb: 0 }} variant="fieldHeader">
              {t('work.description')}
            </Typography>
            <TextField
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              size="small"
              value={activityDescription}
              onChange={(e) => setActivityDescription(e.target.value)}
            />
          </Stack>
        </Box>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} />
      </Box>
    </Box>
  );
}
